import { ClientLoggingService } from '../../../../shared/client-logging.service';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';

import { ActiveTask, TaskCounterService } from '../../../../shared/task-counter.service';
import { TaskStatus } from '../../../../shared/firestore-task-tracking.service';
import { Task, ProgressStep } from '../../../../../generated.commonjs/task_pb';
import { StepLogEntry } from '../async-task-progress/async-task-progress.component';

// Import the excellent existing sub-components
import { TaskProgressHeaderComponent } from '../task-progress-header/task-progress-header.component';
import { TaskProgressBarComponent } from '../task-progress-bar/task-progress-bar.component';
import { TaskActionsComponent } from '../task-actions/task-actions.component';
import { CostAnalysisDisplayComponent } from '../cost-analysis-display/cost-analysis-display.component';
import {
  CostDetailsDialogComponent,
  CostDetailsDialogData,
} from '../cost-details-dialog/cost-details-dialog.component';
import { MatDialog } from '@angular/material/dialog';

/** Total number of backend processing steps (including SERVER_WARMUP). */
const TOTAL_TASK_STEPS = 13;

export type TaskCardData = Task.AsObject & {
  // UI-specific fields for display convenience
  filename?: string; // Extracted from requestMetadata for display (base filename without page number)
  startTime?: Date; // Parsed from createdAt for UI
  endTime?: Date; // Parsed from completedAt/failedAt for UI
  duration?: number; // Calculated from start/end times
  metadata?: { [key: string]: any }; // Parsed metadata for easy access to page numbers, etc.
};

/** Reusable card displaying a single task's status, progress bar, step history, cost analysis, and action buttons (cancel, retry). */
@Component({
  selector: 'app-unified-task-card',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatChipsModule,
    MatExpansionModule,
    MatListModule,
    MatTooltipModule,
    MatDialogModule,
    // Reuse existing excellent sub-components
    TaskProgressHeaderComponent,
    TaskProgressBarComponent,
    TaskActionsComponent,
    CostAnalysisDisplayComponent,
  ],
  templateUrl: './unified-task-card.component.html',
  styleUrls: ['./unified-task-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnifiedTaskCardComponent implements OnInit, OnDestroy, OnChanges {
  private taskCounterService = inject(TaskCounterService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  @Input() task!: TaskCardData;
  @Input() displayMode: 'card' | 'list' | 'compact' = 'card';
  @Input() showProgressDetails: boolean = true;
  @Input() showStepHistory: boolean = false;
  @Input() expandSteps: boolean = false;
  @Input() showStartButton: boolean = false;
  @Input() showTaskActions: boolean = false;

  @Output() retryTask = new EventEmitter<TaskCardData>();
  @Output() cancelTask = new EventEmitter<TaskCardData>();
  @Output() toggleSteps = new EventEmitter<string>();
  @Output() startTask = new EventEmitter<TaskCardData>();
  @Output() dismissCard = new EventEmitter<TaskCardData>();

  // Cached derived data — recomputed only in ngOnChanges, never in templates
  cachedStepHistory: StepLogEntry[] = [];
  cachedHasStepHistory = false;
  cachedHasProgressSteps = false;
  cachedTaskConfig: any = {};
  cachedPageNumbers: number[] = [1];
  cachedFirstPageNumber: number | null = 1;
  cachedIsSinglePage = true;
  cachedFileId: string | null = null;
  cachedCostAnalysis: any = null;
  cachedHasCostAnalysis = false;
  cachedStepElapsedTimes: Map<number, string> = new Map();
  cachedTaskElapsedTime = '--:--';
  cachedQueuingElapsedTime = '';
  cachedStatusIcon = 'help';
  cachedStatusText = 'Unknown';
  cachedStatusClass = 'status-unknown';

  // Timer properties for queuing elapsed time
  private queuingStartTime: Date | null = null;
  private queuingElapsedSeconds = 0;
  private queuingTimerInterval: any;
  private liveTimerInterval: any;

  /**
   * Converts ActiveTask to TaskCardData format
   * Maps ActiveTask fields to the proto Task.AsObject structure with UI enhancements
   */
  static fromActiveTask(activeTask: ActiveTask): TaskCardData {
    // Extract base filename without page number suffix
    let filename = activeTask.filename;
    if (activeTask.metadata && activeTask.metadata['filename']) {
      filename = activeTask.metadata['filename']; // Use base filename from metadata
    }

    // Infer task type using the same logic as TaskStatus, but adapted for ActiveTask
    const inferredTaskType = this.inferTaskTypeFromActiveTask(activeTask);

    return {
      // Task.AsObject proto fields
      taskId: activeTask.taskId,
      taskType: activeTask.taskType || inferredTaskType, // Use actual task type from ActiveTask, or infer it
      status: this.mapStatusToProtoEnum(activeTask.status),
      progress: activeTask.progress,
      message: activeTask.message,
      createdBy: '', // Not available in ActiveTask
      createdAt: this.safeToISOString(activeTask.startTime),
      updatedAt: new Date().toISOString(),
      completedAt: this.safeToISOString(activeTask.endTime),
      failedAt: activeTask.status === 'failed' ? this.safeToISOString(activeTask.endTime) : '',
      cancelledAt: activeTask.status === 'cancelled' ? this.safeToISOString(activeTask.endTime) : '',
      requestMetadata: JSON.stringify(activeTask.metadata || {}),
      resultMap: [], // Not available in ActiveTask
      currentStep: 0, // Default step
      progressStepsList: activeTask.progressSteps || [],
      projectId: activeTask.projectId,
      costAnalysis: activeTask.costAnalysis, // Include cost analysis data
      // UI-specific fields
      filename: filename,
      startTime: activeTask.startTime,
      endTime: activeTask.endTime,
      duration: activeTask.endTime ? activeTask.endTime.getTime() - activeTask.startTime.getTime() : undefined,
      metadata: activeTask.metadata, // Include metadata for page number access
    };
  }

  /**
   * Converts TaskStatus to TaskCardData format
   * Maps TaskStatus to proto Task.AsObject structure with UI enhancements
   */
  static fromTaskStatus(taskStatus: TaskStatus, projectId?: string): TaskCardData {
    // Extract base filename without page number suffix
    let filename = 'Unknown File';
    if (taskStatus.metadata && taskStatus.metadata['filename']) {
      filename = taskStatus.metadata['filename']; // Use base filename from metadata
    }

    return {
      // Task.AsObject proto fields
      taskId: taskStatus.taskId,
      taskType: this.inferTaskType(taskStatus), // Infer from metadata or filename
      status: this.mapStatusToProtoEnum(taskStatus.status),
      progress: taskStatus.progress,
      message: taskStatus.message,
      createdBy: '', // Not available in TaskStatus
      createdAt: taskStatus.createdAt || '',
      updatedAt: taskStatus.updatedAt || '',
      completedAt: taskStatus.completedAt || '',
      failedAt: taskStatus.failedAt || '',
      cancelledAt: taskStatus.cancelledAt || '',
      requestMetadata: JSON.stringify(taskStatus.metadata || {}),
      resultMap: [], // Convert result if available
      currentStep: taskStatus.currentStep || 0,
      progressStepsList: taskStatus.progressSteps || [],
      projectId: projectId || '',
      costAnalysis: taskStatus.costAnalysis, // Include cost analysis data
      // UI-specific fields
      filename: filename,
      startTime: this.safeParseDate(taskStatus.createdAt) || new Date(),
      endTime:
        this.safeParseDate(taskStatus.completedAt) ||
        this.safeParseDate(taskStatus.failedAt) ||
        this.safeParseDate(taskStatus.cancelledAt) ||
        undefined,
      duration: this.calculateDuration(taskStatus),
      metadata: taskStatus.metadata, // Include metadata for page number access
    };
  }

  /**
   * Normalizes task type from various sources (Firestore taskType, metadata, content analysis)
   * Handles both clean types ('compliance-report') and full service names
   * ('org.codetricks...StartPageSectionComplianceReportTask')
   */
  private static normalizeTaskType(
    taskTypeField?: string,
    metadata?: { [key: string]: any },
    message?: string,
    result?: { [key: string]: any },
    progressSteps?: any[],
    filename?: string
  ): string {
    // Priority 1: Check explicit taskType field (handle both formats)
    if (taskTypeField) {
      const normalized = this.parseTaskTypeString(taskTypeField);
      if (normalized !== 'unknown') return normalized;
    }

    // Priority 2: Check metadata
    if (metadata?.['taskType']) {
      const normalized = this.parseTaskTypeString(metadata['taskType']);
      if (normalized !== 'unknown') return normalized;
    }

    // Priority 3: Check message content (very reliable for completed tasks)
    if (message) {
      const messageCheck = message.toLowerCase();
      if (
        messageCheck.includes('compliance report') ||
        messageCheck.includes('compliance analysis') ||
        (messageCheck.includes('compliance') && messageCheck.includes('generated'))
      ) {
        return 'compliance-report';
      }
      if (messageCheck.includes('applicability') || messageCheck.includes('code analysis')) {
        return 'code-applicability';
      }
    }

    // Priority 4: Check result data structure (for completed tasks)
    if (result) {
      const resultKeys = Object.keys(result).join(' ').toLowerCase();
      if (
        resultKeys.includes('compliance_statement') ||
        resultKeys.includes('compliance_status') ||
        resultKeys.includes('icc_section')
      ) {
        return 'compliance-report';
      }
    }

    // Priority 5: Check progress steps (may not be available for completed tasks)
    if (progressSteps && progressSteps.length > 0) {
      const stepDescriptions = progressSteps.map((step: any) => step.description?.toLowerCase() || '').join(' ');

      if (
        stepDescriptions.includes('compliance report') ||
        stepDescriptions.includes('compliance analysis') ||
        stepDescriptions.includes('initialize compliance') ||
        stepDescriptions.includes('analyze building code') ||
        stepDescriptions.includes('evaluate compliance') ||
        stepDescriptions.includes('generate detailed compliance') ||
        stepDescriptions.includes('save compliance report')
      ) {
        return 'compliance-report';
      }

      if (
        stepDescriptions.includes('code applicability') ||
        stepDescriptions.includes('applicability analysis') ||
        stepDescriptions.includes('analyze code sections')
      ) {
        return 'code-applicability';
      }
    }

    // Priority 6: Check filename
    if (filename) {
      const filenameCheck = filename.toLowerCase();
      if (filenameCheck.includes('compliance report')) {
        return 'compliance-report';
      }
      if (filenameCheck.includes('code applicability') || filenameCheck.includes('analysis')) {
        return 'code-applicability';
      }
    }

    return 'plan_ingestion'; // Default fallback
  }

  /**
   * Parses a taskType string and normalizes it to standard format
   * Handles both clean types and full Java service method names
   */
  private static parseTaskTypeString(taskTypeStr: string): string {
    if (!taskTypeStr) return 'unknown';

    const lowerStr = taskTypeStr.toLowerCase();

    // Handle already-normalized types
    if (lowerStr === 'compliance-report' || lowerStr === 'code-applicability' || lowerStr === 'plan_ingestion') {
      return lowerStr;
    }

    // Handle full service method names (the current Firestore format)
    if (lowerStr.includes('compliancereport') || lowerStr.includes('compliance')) {
      return 'compliance-report';
    }
    if (lowerStr.includes('applicability') || lowerStr.includes('codeanalysis')) {
      return 'code-applicability';
    }
    if (lowerStr.includes('ingestion') || lowerStr.includes('ingest')) {
      return 'plan_ingestion';
    }

    return 'unknown';
  }

  /**
   * Infers task type from ActiveTask using the normalized approach
   */
  private static inferTaskTypeFromActiveTask(activeTask: ActiveTask): string {
    return this.normalizeTaskType(
      activeTask.taskType,
      activeTask.metadata,
      activeTask.message,
      undefined, // ActiveTask doesn't have result field
      activeTask.progressSteps,
      activeTask.filename
    );
  }

  /**
   * Infers task type from TaskStatus using the normalized approach
   */
  private static inferTaskType(taskStatus: TaskStatus): string {
    return this.normalizeTaskType(
      taskStatus.taskType,
      taskStatus.metadata,
      taskStatus.message,
      taskStatus.result,
      taskStatus.progressSteps,
      taskStatus.metadata?.['filename']
    );
  }

  /**
   * Safely parses a date string to Date object, handling invalid dates
   */
  private static safeParseDate(dateString: string | undefined | null): Date | undefined {
    if (!dateString) return undefined;

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        ClientLoggingService.log('WARN', 'Invalid date string detected:', dateString);
        return undefined;
      }
      return date;
    } catch (error) {
      ClientLoggingService.log('WARN', 'Error parsing date string:', error, dateString);
      return undefined;
    }
  }

  /**
   * Safely converts a Date to ISO string, handling invalid dates
   */
  private static safeToISOString(date: Date | undefined | null): string {
    if (!date) return '';

    try {
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        ClientLoggingService.log('WARN', 'Invalid date object detected:', date);
        return '';
      }
      return date.toISOString();
    } catch (error) {
      ClientLoggingService.log('WARN', 'Error converting date to ISO string:', error, date);
      return '';
    }
  }

  /**
   * Calculates task duration from timestamps
   */
  private static calculateDuration(taskStatus: TaskStatus): number | undefined {
    if (!taskStatus.createdAt) return undefined;

    try {
      const startTime = new Date(taskStatus.createdAt);
      if (isNaN(startTime.getTime())) return undefined;

      let endTime: Date | undefined;

      if (taskStatus.completedAt) {
        endTime = new Date(taskStatus.completedAt);
      } else if (taskStatus.failedAt) {
        endTime = new Date(taskStatus.failedAt);
      } else if (taskStatus.cancelledAt) {
        endTime = new Date(taskStatus.cancelledAt);
      }

      if (endTime && !isNaN(endTime.getTime())) {
        return endTime.getTime() - startTime.getTime();
      }
    } catch (error) {
      ClientLoggingService.log('WARN', 'Error calculating task duration:', error);
    }

    return undefined;
  }

  /**
   * Maps string status to proto enum
   */
  private static mapStatusToProtoEnum(status: string): Task.TaskStatus {
    switch (status) {
      case 'pending':
        return Task.TaskStatus.PENDING;
      case 'processing':
        return Task.TaskStatus.PROCESSING;
      case 'complete':
        return Task.TaskStatus.COMPLETE;
      case 'failed':
        return Task.TaskStatus.FAILED;
      case 'cancelled':
        return Task.TaskStatus.CANCELLED;
      default:
        return Task.TaskStatus.PENDING;
    }
  }

  getStatusIcon(): string {
    switch (this.task.status) {
      case Task.TaskStatus.PENDING:
        return 'schedule';
      case Task.TaskStatus.PROCESSING:
        return 'sync';
      case Task.TaskStatus.COMPLETE:
        return 'check_circle';
      case Task.TaskStatus.FAILED:
        return 'error';
      case Task.TaskStatus.CANCELLED:
        return 'cancel';
      default:
        return 'help';
    }
  }

  getStatusText(): string {
    switch (this.task.status) {
      case Task.TaskStatus.PENDING:
        return 'Pending';
      case Task.TaskStatus.PROCESSING:
        return 'Processing';
      case Task.TaskStatus.COMPLETE:
        return 'Complete';
      case Task.TaskStatus.FAILED:
        return 'Failed';
      case Task.TaskStatus.CANCELLED:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(): string {
    switch (this.task.status) {
      case Task.TaskStatus.PENDING:
        return 'status-pending';
      case Task.TaskStatus.PROCESSING:
        return 'status-processing';
      case Task.TaskStatus.COMPLETE:
        return 'status-complete';
      case Task.TaskStatus.FAILED:
        return 'status-failed';
      case Task.TaskStatus.CANCELLED:
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  }

  isActive(): boolean {
    return this.task.status === Task.TaskStatus.PENDING || this.task.status === Task.TaskStatus.PROCESSING;
  }

  isCompleted(): boolean {
    return this.task.status === Task.TaskStatus.COMPLETE;
  }

  isFailed(): boolean {
    return this.task.status === Task.TaskStatus.FAILED;
  }

  isCancelled(): boolean {
    return this.task.status === Task.TaskStatus.CANCELLED;
  }

  /** Formats the task duration in MM:SS from the elapsed milliseconds. */
  public formatTaskDuration(): string {
    if (!this.task.duration) return '--:--';

    const seconds = Math.floor(this.task.duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // Get elapsed time for tasks (live for active, fixed for completed)
  getTaskElapsedTime(): string {
    if (!this.task.startTime) return '--:--';

    const startTime = new Date(this.task.startTime);
    let endTime: Date;

    // For completed tasks, use the actual end time; for active tasks, use current time
    if (this.isCompleted() || this.isFailed() || this.isCancelled()) {
      if (this.task.endTime) {
        endTime = new Date(this.task.endTime);
      } else {
        // Fallback: if no endTime, don't show elapsed time for completed tasks
        return '--:--';
      }
    } else {
      // For active tasks, show live elapsed time
      endTime = new Date();
    }

    const elapsedMs = endTime.getTime() - startTime.getTime();

    // Validate the calculation
    if (isNaN(elapsedMs) || elapsedMs < 0) {
      return '--:--';
    }

    const seconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  onRetry(): void {
    this.retryTask.emit(this.task);
  }

  onCancel(): void {
    // Call the actual cancel functionality instead of just emitting
    this.onCancelTask();
  }

  /**
   * Cancels the task by updating its status in Firestore
   */
  async onCancelTask(): Promise<void> {
    if (!this.isActive()) {
      ClientLoggingService.log('WARN', '[UnifiedTaskCard] Cannot cancel non-active task:', this.task.taskId);
      return;
    }

    try {
      ClientLoggingService.log('INFO', '[UnifiedTaskCard] Cancelling task:', this.task.taskId);
      const success = await this.taskCounterService.cancelTask(this.task.taskId, 'Cancelled by user');

      if (success) {
        ClientLoggingService.log('INFO', '[UnifiedTaskCard] Task cancelled successfully:', this.task.taskId);
      } else {
        ClientLoggingService.log('ERROR', '[UnifiedTaskCard] Failed to cancel task:', this.task.taskId);
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[UnifiedTaskCard] Error cancelling task:', error);
    }
  }

  onToggleSteps(): void {
    this.toggleSteps.emit(this.task.taskId);
  }

  hasProgressSteps(): boolean {
    return !!(this.task.progressStepsList && this.task.progressStepsList.length > 0);
  }

  /**
   * Checks if task has cost analysis data
   */
  hasCostAnalysis(): boolean {
    return !!(
      this.task.costAnalysis &&
      (this.task.costAnalysis.totalCostUsd > 0 || this.task.costAnalysis.totalTokens > 0)
    );
  }

  /**
   * Gets the cost analysis data for display
   */
  getCostAnalysis() {
    return this.task.costAnalysis || null;
  }

  /**
   * Opens the cost details dialog
   */
  onOpenCostDetails(costAnalysis: any): void {
    const dialogData: CostDetailsDialogData = {
      costAnalysis: costAnalysis,
      taskId: this.task.taskId,
      taskTitle: this.task.filename || 'Task Details',
      projectId: this.task.projectId,
    };

    this.dialog.open(CostDetailsDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: dialogData,
      panelClass: 'cost-details-dialog-panel',
    });
  }

  getCurrentStep(): string {
    if (!this.hasProgressSteps()) return '';

    const runningStep = this.task.progressStepsList!.find((step: any) => step.status === 1); // IN_PROGRESS
    if (runningStep) return runningStep.name;

    const completedSteps = this.task.progressStepsList!.filter((step: any) => step.status === 2); // COMPLETED
    if (completedSteps.length > 0) {
      const lastCompleted = completedSteps[completedSteps.length - 1];
      return `✅ ${lastCompleted.name}`;
    }

    return '';
  }

  // Methods to support existing sub-components

  /**
   * Gets task config for TaskProgressHeaderComponent
   */
  getTaskConfig(): any {
    return {
      projectId: this.task.projectId,
      filename: this.task.filename,
      taskType: this.task.taskType, // Include the actual task type from Firestore
      pageNumbers: this.getPageNumbers(),
      forceReprocess: false,
      enableOrientationDetection: false,
    };
  }

  /**
   * Gets page numbers from task metadata
   */
  getPageNumbers(): number[] {
    // Extract page number from task metadata
    if (this.task.metadata) {
      const pageNumber = this.task.metadata['pageNumber'] || this.task.metadata['page_number'];
      if (pageNumber && typeof pageNumber === 'number') {
        return [pageNumber];
      }
    }
    return [1]; // Default fallback
  }

  /**
   * Gets first page number
   */
  getFirstPageNumber(): number | null {
    const pages = this.getPageNumbers();
    return pages.length > 0 ? pages[0] : null;
  }

  /**
   * Checks if single page task
   */
  isSinglePage(): boolean {
    return this.getPageNumbers().length === 1;
  }

  /**
   * Gets queuing elapsed time formatted for display
   */
  getQueuingElapsedTime(): string {
    if (this.queuingElapsedSeconds === 0) return '';

    if (this.queuingElapsedSeconds < 60) {
      return `${this.queuingElapsedSeconds}s`;
    } else {
      const minutes = Math.floor(this.queuingElapsedSeconds / 60);
      const seconds = this.queuingElapsedSeconds % 60;
      return `${minutes}m ${seconds}s`;
    }
  }

  /**
   * Gets step history - now used directly by the component
   * Converts proto ProgressStep.AsObject[] to StepLogEntry[] format
   */
  getStepHistory(): StepLogEntry[] {
    if (this.isActive() && (!this.task.progressStepsList || this.task.progressStepsList.length === 0)) {
      return [
        {
          stepNumber: 1,
          totalSteps: TOTAL_TASK_STEPS,
          description: '🔥 Server warm-up',
          status: 'running',
          startTime: this.task.startTime || new Date(),
          endTime: undefined,
          duration: undefined,
        },
      ];
    }

    if (!this.task.progressStepsList || this.task.progressStepsList.length === 0) {
      return [];
    }

    return this.task.progressStepsList.map((step: any) => {
      let duration: number | undefined = undefined;
      if (step.durationMs) {
        const parsedDuration = typeof step.durationMs === 'string' ? parseInt(step.durationMs, 10) : step.durationMs;
        duration = isNaN(parsedDuration) ? undefined : parsedDuration;
      }

      let startTime = new Date();
      let endTime: Date | undefined = undefined;

      if (step.startedAt) {
        const parsedStartTime = new Date(step.startedAt);
        if (!isNaN(parsedStartTime.getTime())) {
          startTime = parsedStartTime;
        }
      }

      if (step.completedAt) {
        const parsedEndTime = new Date(step.completedAt);
        if (!isNaN(parsedEndTime.getTime())) {
          endTime = parsedEndTime;
        }
      }

      return {
        stepNumber: step.stepNumber || 0,
        totalSteps: step.totalSteps || 0,
        description: step.name || step.description || 'Unknown step',
        status: this.mapProtoStepStatusToString(step.status),
        startTime: startTime,
        endTime: endTime,
        duration: duration,
      };
    });
  }

  /**
   * Checks if task has step history to display
   */
  hasStepHistory(): boolean {
    return this.getStepHistory().length > 0;
  }

  /**
   * Gets the current (most recent) step from the step history
   */
  getCurrentStepEntry(): StepLogEntry | undefined {
    const stepHistory = this.getStepHistory();
    if (stepHistory.length === 0) return undefined;

    // Return the last step in the history (most recent)
    return stepHistory[stepHistory.length - 1];
  }

  /**
   * Gets the most recent step for display in compact view
   */
  getMostRecentStep(): StepLogEntry | null {
    const steps = this.getStepHistory();
    if (steps.length === 0) return null;
    return steps[steps.length - 1];
  }

  /**
   * Gets display name for the task (e.g., "Page 41")
   */
  getDisplayName(): string {
    const pageNumber = this.getFirstPageNumber();
    return pageNumber ? `Page ${pageNumber}` : 'Task';
  }

  /**
   * Gets the step status class for styling
   */
  getStepStatusClass(step: StepLogEntry): string {
    switch (step.status) {
      case 'completed':
        return 'step-completed';
      case 'failed':
        return 'step-failed';
      case 'running':
        return 'step-running';
      default:
        return 'step-pending';
    }
  }

  /**
   * Gets the step icon
   */
  getStepIcon(step: StepLogEntry): string {
    switch (step.status) {
      case 'completed':
        return 'check_circle';
      case 'failed':
        return 'error';
      case 'running':
        return 'settings';
      default:
        return 'radio_button_unchecked';
    }
  }

  /**
   * Gets elapsed time for a step with live updates
   */
  getElapsedTime(step: StepLogEntry): string {
    if (step.status === 'completed' && step.duration !== undefined && !isNaN(step.duration)) {
      return this.formatDuration(step.duration);
    }

    if (step.endTime && step.startTime && !isNaN(step.endTime.getTime()) && !isNaN(step.startTime.getTime())) {
      const duration = step.endTime.getTime() - step.startTime.getTime();
      if (!isNaN(duration) && duration >= 0) {
        return this.formatDuration(duration);
      }
    }

    if (step.startTime && step.status === 'running' && !isNaN(step.startTime.getTime())) {
      const duration = Date.now() - step.startTime.getTime();
      if (!isNaN(duration) && duration >= 0) {
        return this.formatDuration(duration);
      }
    }

    return '--:--';
  }

  /**
   * Formats duration in MM:SS format
   */
  private formatDuration(durationMs: number): string {
    // Validate input
    if (isNaN(durationMs) || durationMs < 0) {
      return '--:--';
    }

    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    // Additional validation for the calculated values
    if (isNaN(minutes) || isNaN(seconds)) {
      return '--:--';
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Maps proto ProgressStep.StepStatus enum or string to string format expected by StepLogEntry
   */
  private mapProtoStepStatusToString(
    status: ProgressStep.StepStatus | string | any
  ): 'running' | 'completed' | 'failed' {
    // Handle string values from Firestore
    if (typeof status === 'string') {
      switch (status.toUpperCase()) {
        case 'COMPLETED':
          return 'completed';
        case 'FAILED':
          return 'failed';
        case 'IN_PROGRESS':
        case 'RUNNING':
          return 'running';
        default:
          return 'running'; // Default for unknown states
      }
    }

    // Handle enum values from proto
    switch (status) {
      case ProgressStep.StepStatus.COMPLETED:
        return 'completed';
      case ProgressStep.StepStatus.FAILED:
        return 'failed';
      case ProgressStep.StepStatus.IN_PROGRESS:
        return 'running';
      default:
        return 'running'; // Default for unknown states
    }
  }

  /**
   * Maps proto enum to string for compatibility with existing components
   */
  private mapProtoEnumToString(status: Task.TaskStatus): string {
    switch (status) {
      case Task.TaskStatus.PENDING:
        return 'pending';
      case Task.TaskStatus.PROCESSING:
        return 'processing';
      case Task.TaskStatus.COMPLETE:
        return 'complete';
      case Task.TaskStatus.FAILED:
        return 'failed';
      default:
        return 'pending';
    }
  }

  /**
   * Event handlers for sub-components
   */
  onStartTask(): void {
    this.startTask.emit(this.task);
  }

  onDismissCard(): void {
    this.dismissCard.emit(this.task);
  }

  onToggleTaskStepsExpansion(taskId: string): void {
    this.toggleSteps.emit(taskId);
  }

  /**
   * Handles step expansion toggle - now handled internally
   */
  onToggleStepsExpansion(): void {
    this.toggleSteps.emit(this.task.taskId);
  }

  /**
   * Component lifecycle methods
   */
  ngOnInit(): void {
    this.refreshCachedData();
    if (this.isActive() && this.task.progress === 0 && !this.queuingTimerInterval) {
      this.startQueuingTimer();
    }
    if (this.isActive() && !this.liveTimerInterval) {
      this.startLiveTimer();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['task'] && changes['task'].currentValue) {
      const currentTask = changes['task'].currentValue as TaskCardData;
      const previousTask = changes['task'].previousValue as TaskCardData;

      this.refreshCachedData();

      if (previousTask && previousTask.progress === 0 && currentTask.progress > 0 && this.queuingTimerInterval) {
        this.stopQueuingTimer();
      }

      if (this.isActive() && currentTask.progress === 0 && !this.queuingTimerInterval) {
        this.startQueuingTimer();
      }

      if (!this.isActive() && this.queuingTimerInterval) {
        this.stopQueuingTimer();
      }

      if (this.isActive() && !this.liveTimerInterval) {
        this.startLiveTimer();
      } else if (!this.isActive() && this.liveTimerInterval) {
        this.stopLiveTimer();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopQueuingTimer();
    this.stopLiveTimer();
  }

  /**
   * Recomputes all cached derived data from the current task input.
   * Called once per ngOnChanges, never from the template.
   */
  private refreshCachedData(): void {
    this.cachedStatusIcon = this.getStatusIcon();
    this.cachedStatusText = this.getStatusText();
    this.cachedStatusClass = this.getStatusClass();
    this.cachedStepHistory = this.getStepHistory();
    this.cachedHasStepHistory = this.cachedStepHistory.length > 0;
    this.cachedHasProgressSteps = this.hasProgressSteps();
    this.cachedPageNumbers = this.getPageNumbers();
    this.cachedFirstPageNumber = this.cachedPageNumbers.length > 0 ? this.cachedPageNumbers[0] : null;
    this.cachedIsSinglePage = this.cachedPageNumbers.length === 1;
    this.cachedFileId = this.getFileIdFromTask();
    this.cachedCostAnalysis = this.task.costAnalysis || null;
    this.cachedHasCostAnalysis = !!(
      this.cachedCostAnalysis &&
      (this.cachedCostAnalysis.totalCostUsd > 0 || this.cachedCostAnalysis.totalTokens > 0)
    );
    this.cachedTaskConfig = {
      projectId: this.task.projectId,
      filename: this.task.filename,
      taskType: this.task.taskType,
      pageNumbers: this.cachedPageNumbers,
      forceReprocess: false,
      enableOrientationDetection: false,
    };
    this.refreshTimingData();
  }

  /** Recomputes only timing data (called by live timer and refreshCachedData). */
  private refreshTimingData(): void {
    this.cachedTaskElapsedTime = this.getTaskElapsedTime();
    this.cachedQueuingElapsedTime = this.getQueuingElapsedTime();
    this.cachedStepElapsedTimes.clear();
    for (const step of this.cachedStepHistory) {
      this.cachedStepElapsedTimes.set(step.stepNumber, this.getElapsedTime(step));
    }
  }

  /** Gets the cached elapsed time for a step (used in template). */
  getStepElapsedTimeCached(stepNumber: number): string {
    return this.cachedStepElapsedTimes.get(stepNumber) ?? '--:--';
  }

  /**
   * Timer management methods
   */
  private startQueuingTimer(): void {
    this.queuingStartTime = new Date();
    this.queuingElapsedSeconds = 0;

    this.ngZone.runOutsideAngular(() => {
      this.queuingTimerInterval = setInterval(() => {
        if (this.queuingStartTime) {
          this.queuingElapsedSeconds = Math.floor((new Date().getTime() - this.queuingStartTime.getTime()) / 1000);
          this.cachedQueuingElapsedTime = this.getQueuingElapsedTime();
          this.cdr.detectChanges();
        }
      }, 1000);
    });
  }

  private stopQueuingTimer(): void {
    if (this.queuingTimerInterval) {
      clearInterval(this.queuingTimerInterval);
      this.queuingTimerInterval = null;
    }
    this.queuingStartTime = null;
    this.queuingElapsedSeconds = 0;
  }

  /** Starts a 1-second interval outside Angular zone to update live elapsed times. */
  private startLiveTimer(): void {
    this.ngZone.runOutsideAngular(() => {
      this.liveTimerInterval = setInterval(() => {
        this.refreshTimingData();
        this.cdr.detectChanges();
      }, 1000);
    });
  }

  private stopLiveTimer(): void {
    if (this.liveTimerInterval) {
      clearInterval(this.liveTimerInterval);
      this.liveTimerInterval = null;
    }
  }

  /**
   * Extracts file ID from task metadata for multi-file support
   * Issue #167: Used to display correct file information in task headers
   */
  getFileIdFromTask(): string | null {
    try {
      // Try to parse requestMetadata JSON to get file ID
      if (this.task.requestMetadata) {
        const metadata = JSON.parse(this.task.requestMetadata);
        return metadata.fileId || null;
      }

      // Fallback: check if metadata has fileId directly
      if (this.task.metadata && this.task.metadata['fileId']) {
        return this.task.metadata['fileId'];
      }

      return null;
    } catch (error) {
      ClientLoggingService.log('WARN', '[UnifiedTaskCardComponent] Error extracting file ID from task:', error);
      return null;
    }
  }
}
